import { apiGet } from '@/services/api';
import { newsFilters } from '@/features/publicsite/news/newsFilters';
import type { NewsItemsEnvelope } from '@/features/publicsite/types/NewsItemsEnvelope';
import type { NewsPageResponse } from '@/features/publicsite/types/news/NewsPageResponse';

const NEWS = '/api/v1/public/news';

/** How many stories a page of /stiri holds. */
export const NEWS_PAGE_SIZE = 12;

/**
 * The listing request for a page of the current filters.
 *
 * `includeArticles` stays: /stiri has always listed news and articles together (see
 * PublicApiController.news), and without it the endpoint answers with news alone.
 */
export function newsQuery(params: URLSearchParams, page: number, size = NEWS_PAGE_SIZE): string {
  const search = new URLSearchParams({
    page: String(page),
    size: String(size),
    includeArticles: 'true',
  });
  Object.entries(newsFilters(params)).forEach(([name, value]) => search.set(name, value));
  return `${NEWS}?${search}`;
}

/**
 * Every request the public news listing makes — /stiri, the related stories under a story and the
 * home page's strip call these, never a URL.
 */
export const publicNewsApi = {
  /** Every category the archive has, for the category filter. */
  categories: () => apiGet<string[]>('/api/v1/public/news-categories'),
  /**
   * A page of /stiri, by the address newsQuery built. The page keys its effect on that same
   * string, so the request that is sent cannot drift from the one the page is waiting for.
   */
  page: (request: string) => apiGet<NewsPageResponse>(request),
  /** A few cards of the listing, narrowed by `filters` — the related stories' requests. */
  cards: (filters: Record<string, string>) =>
    apiGet<NewsItemsEnvelope>(
      `${NEWS}?${new URLSearchParams({ includeArticles: 'true', page: '0', ...filters })}`,
    ),
  /**
   * The home strip's three latest. An address rather than a fetcher: it is read through
   * usePublicData, which keys its request on the address and the language.
   */
  latestUrl: `${NEWS}?page=0&size=3&includeArticles=true`,
};
